import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { I18nextProvider } from 'react-i18next';
import Script from 'next/script';
import { ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { loadDevMessages } from '@apollo/client/dev';
import { type i18n } from 'i18next';
import { ClientWrapper } from '@/components/ClientWrapper';
import { GlobalStyles } from '@/styles/globalStyles';
import { BlockerDetector } from '@/components/Banners/BlockerDetector';
import NoSsr from '@/components/NoSsr/NoSsr';
import { BannersStyles } from '@/components/Banners/Banners.styles';
import { createApolloClient } from '@/config/apollo-client';
import { isProduction } from '@/utils/helpers';
import { PubNubInit } from '@/components/PubNubInit/PubNubInit';
import { AuthCheck } from '@/components/PubNubInit/AuthCheck';
import { BridgeProvider } from '@/bridge/BridgeProvider';
import { ThemeProviderWrapper } from '@/hooks/theme/wrapper';
import { Seo } from '@/components/Seo/Seo';
import { SeoFullData } from '@/api/types/seo.types';
import { PageContextProvider } from '@/contexts';
import { Device, DeviceType } from '@/utils/getDeviceType';
import { Languages } from '@/constants/common';
import { useClientI18n } from '@/hooks/useClientI18n';
import { ROUTES } from '@/constants/routes';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { AuthStatus } from '@/store/useAuthStore';
import { logError } from '@/utils/log-error';

if (!isProduction()) {
  loadDevMessages();
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (
    typeof window === 'undefined' ||
    window.location.pathname !== ROUTES.home
  ) {
    return;
  }

  const isAuthenticated =
    localStorage.getItem(STORAGE_KEYS.AUTH_STATUS) ===
    AuthStatus.Authenticated.toString();

  if (metric.name === 'CLS' && metric.value >= 0.2) {
    try {
      const windowSize = `${window.innerWidth}x${window.innerHeight}`;
      const metricStr = JSON.stringify(metric);
      const message = `[CLS] window size: ${windowSize}, scrollY: ${window.scrollY}, isAuthenticated: ${isAuthenticated}, metric: ${metricStr}`;
      logError(message);
    } catch (e) {
      console.error(e);
    }
  }
}

function App({
  Component,
  pageProps,
}: AppProps<{
  currentLang: Languages;
  i18nServerInstance: i18n;
  isSSRWebView: boolean;
  seoFullData: SeoFullData;
  apolloState?: NormalizedCacheObject | null;
  accessToken?: string;
  device: Device;
  deviceType: DeviceType;
}>) {
  const isBrowser = typeof window !== 'undefined';
  const i18nInstance = useClientI18n(pageProps.currentLang);

  const apolloClient = createApolloClient({
    lang: pageProps.currentLang || i18nInstance.language,
    initialState: pageProps.apolloState,
  });

  return (
    <>
      <Script src="/scripts/adriver.core.2.min.js" />
      <Seo {...pageProps.seoFullData} />
      <BridgeProvider isWebView={pageProps.isSSRWebView}>
        <ThemeProviderWrapper isWebView={pageProps.isSSRWebView}>
          <GlobalStyles />
          <BannersStyles />
          <I18nextProvider
            i18n={isBrowser ? i18nInstance : pageProps.i18nServerInstance}
          >
            <ApolloProvider client={apolloClient}>
              <PageContextProvider
                device={pageProps.device}
                deviceType={pageProps.deviceType}
              >
                <ClientWrapper
                  isSSRWebView={pageProps.isSSRWebView}
                  accessToken={pageProps.accessToken}
                >
                  <AuthCheck />
                  <PubNubInit />
                  <NoSsr>
                    <BlockerDetector />
                  </NoSsr>
                  <Component {...pageProps} />
                </ClientWrapper>
              </PageContextProvider>
            </ApolloProvider>
          </I18nextProvider>
        </ThemeProviderWrapper>
      </BridgeProvider>
    </>
  );
}

export default App;
